import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchSeoCommand, saveSeoLocal, createLocalPage } from "@/lib/dashboardApi";
import {
  Target, Loader2, AlertTriangle, Link2, FileWarning, Layers, TrendingUp, Lock,
  Grid3x3, Unlink, MapPin, CheckCircle2, XCircle, Save, Plus, Trash2, Building2,
} from "lucide-react";

export default function SeoCommand() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["seo-command"], queryFn: fetchSeoCommand });
  const [gbp, setGbp] = useState(null);
  const [citations, setCitations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(null);

  const createCell = async (ss, cs) => {
    setCreating(`${ss}|${cs}`);
    try {
      const r = await createLocalPage(ss, cs);
      toast.success(`Local page created: ${r.url}`);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create page."); }
    setCreating(null);
  };

  useEffect(() => {
    if (data?.gbp) setGbp({ profile_url: data.gbp.profile_url || "", notes: data.gbp.notes || "",
      checklist: Object.fromEntries((data.gbp.items || []).map((i) => [i.id, i.done])) });
    if (data?.nap?.citations) setCitations(data.nap.citations);
  }, [data]);

  if (isLoading || !data || !gbp) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const toggleGbp = (id) => setGbp((g) => ({ ...g, checklist: { ...g.checklist, [id]: !g.checklist[id] } }));
  const gbpItems = data.gbp.items.map((i) => ({ ...i, done: !!gbp.checklist[i.id] }));
  const gbpDone = gbpItems.filter((i) => i.done).length;

  const saveLocal = async () => {
    setSaving(true);
    try {
      await saveSeoLocal({ gbp: { profile_url: gbp.profile_url, notes: gbp.notes, checklist: gbp.checklist },
        nap_citations: citations });
      toast.success("Local SEO settings saved.");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    setSaving(false);
  };

  const cov = data.coverage_matrix;
  const coveredSet = new Set(cov.covered);

  return (
    <div data-testid="seo-command-root">
      <h1 className="text-2xl font-bold text-navy flex items-center gap-2"><Target className="h-6 w-6 text-launch" /> Brevard SEO Command Center</h1>
      <p className="text-slate-500 mt-1 mb-6">City × service coverage, content health, orphan links, and local (GBP/NAP) status — computed from your live pages.</p>

      <div className="flex flex-wrap gap-4 mb-6">
        <Stat label="Service pages" value={data.counts.services} />
        <Stat label="City pages" value={data.counts.locations} />
        <Stat label="Blog posts" value={data.counts.blog} />
        <Stat label="Orphan pages" value={data.counts.orphans} tone={data.counts.orphans ? "warn" : "ok"} />
        <Stat label="NAP score" value={`${data.nap.consistency_score}%`} tone={data.nap.consistency_score >= 100 ? "ok" : "warn"} />
        <Stat label="GBP complete" value={`${data.gbp.completion}%`} tone={data.gbp.completion >= 80 ? "ok" : "warn"} />
      </div>

      {/* Priority actions */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="seo-actions">
        <h3 className="font-bold text-navy flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-launch" /> Today's Priority Actions</h3>
        {data.priority_actions.length === 0 ? <p className="text-sm text-slate-400">No on-site issues detected — nice.</p> : (
          <ol className="space-y-2">
            {data.priority_actions.map((a, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-navy/5 text-navy border border-navy/10 shrink-0 mt-0.5">{a.priority}</span>
                <span className="text-slate-700">{a.action}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* City × Service coverage matrix */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="coverage-matrix">
        <h3 className="font-bold text-navy flex items-center gap-2 mb-1"><Grid3x3 className="h-4 w-4 text-launch" /> City × Service Coverage</h3>
        <p className="text-xs text-slate-500 mb-4">Green = a dedicated local landing page exists in the sitemap for that service in that city. Click a grey cell to create one in one click.</p>
        <div className="overflow-x-auto">
          <table className="text-xs border-separate" style={{ borderSpacing: 0 }}>
            <thead>
              <tr>
                <th className="sticky left-0 bg-white px-2 py-2 text-left text-slate-500 font-semibold min-w-[150px]">Service \ City</th>
                {cov.cities.map((c) => (
                  <th key={c.slug} className="px-2 py-2 text-slate-500 font-semibold whitespace-nowrap">{c.city}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cov.services.map((s) => (
                <tr key={s.slug}>
                  <td className="sticky left-0 bg-white px-2 py-1.5 font-medium text-slate-700 whitespace-nowrap border-t border-slate-100">
                    {s.name}{!s.combo && <span className="ml-1 text-[9px] text-slate-400">(no local pages)</span>}
                  </td>
                  {cov.cities.map((c) => {
                    const on = coveredSet.has(`${s.slug}|${c.slug}`);
                    const key = `${s.slug}|${c.slug}`;
                    return (
                      <td key={c.slug} className="px-2 py-1.5 text-center border-t border-slate-100">
                        {on ? (
                          <span className="inline-block h-4 w-4 rounded bg-emerald-500"
                            title={`/${s.slug}/${c.slug}`} data-testid={`cov-${s.slug}-${c.slug}`} />
                        ) : (
                          <button onClick={() => createCell(s.slug, c.slug)} disabled={creating === key}
                            title={`Create local page: /${s.slug}/${c.slug}`} data-testid={`cov-create-${s.slug}-${c.slug}`}
                            className="inline-flex items-center justify-center h-4 w-4 rounded bg-slate-200 hover:bg-launch text-white transition-colors group disabled:opacity-60">
                            {creating === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3 opacity-0 group-hover:opacity-100" />}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-3">{cov.covered_cells} of {cov.total_cells} city×service combinations have a dedicated local page.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* NAP consistency */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="nap-panel">
          <h3 className="font-bold text-navy flex items-center gap-2 mb-3"><MapPin className="h-4 w-4 text-launch" /> NAP Consistency <span className="text-xs font-normal text-slate-400">({data.nap.consistency_score}%)</span></h3>
          <div className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3 mb-3 space-y-0.5">
            <p><span className="font-semibold">Name:</span> {data.nap.name || "—"}</p>
            <p><span className="font-semibold">Address:</span> {data.nap.address || "—"} {data.nap.postal_code || ""}</p>
            <p><span className="font-semibold">Phone:</span> {data.nap.phone || "—"}</p>
          </div>
          <ul className="space-y-1.5 mb-4">
            {data.nap.checks.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                {c.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />}
                <span className="text-slate-700">{c.label}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Directory citations (track off-site consistency)</p>
          <div className="space-y-2">
            {citations.map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`citation-row-${i}`}>
                <input value={c.name || ""} onChange={(e) => setCitations((cs) => cs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                  placeholder="Directory (e.g. Yelp)" className="flex-1 min-w-0 text-xs border border-slate-200 rounded px-2 py-1" />
                <input value={c.url || ""} onChange={(e) => setCitations((cs) => cs.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                  placeholder="URL" className="flex-1 min-w-0 text-xs border border-slate-200 rounded px-2 py-1" />
                <label className="flex items-center gap-1 text-[11px] text-slate-600 shrink-0">
                  <input type="checkbox" checked={!!c.matches} onChange={(e) => setCitations((cs) => cs.map((x, j) => j === i ? { ...x, matches: e.target.checked } : x))} /> match
                </label>
                <button onClick={() => setCitations((cs) => cs.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button onClick={() => setCitations((cs) => [...cs, { name: "", url: "", matches: false }])} data-testid="add-citation"
              className="inline-flex items-center gap-1 text-xs font-semibold text-launch hover:underline"><Plus className="h-3.5 w-3.5" /> Add citation</button>
          </div>
        </div>

        {/* GBP checklist */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="gbp-panel">
          <h3 className="font-bold text-navy flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-launch" /> Google Business Profile <span className="text-xs font-normal text-slate-400">({gbpDone}/{gbpItems.length})</span></h3>
          <p className="text-xs text-slate-500 mb-3">Manual optimization checklist. A live Google Business Profile API connection is planned — until then, track status here.</p>
          <input value={gbp.profile_url} onChange={(e) => setGbp((g) => ({ ...g, profile_url: e.target.value }))}
            placeholder="Google Business Profile URL" data-testid="gbp-url"
            className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 mb-3" />
          <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {gbpItems.map((i) => (
              <li key={i.id}>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`gbp-item-${i.id}`}>
                  <input type="checkbox" checked={i.done} onChange={() => toggleGbp(i.id)} className="mt-0.5" />
                  <span className={i.done ? "text-slate-400 line-through" : "text-slate-700"}>{i.label}</span>
                </label>
              </li>
            ))}
          </ul>
          <span className="inline-block mt-3 text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-500">API connection: coming soon</span>
        </div>
      </div>

      <div className="flex justify-end mb-6">
        <button onClick={saveLocal} disabled={saving} data-testid="save-local-seo"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-white text-sm font-bold hover:bg-navy/90 disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save local SEO
        </button>
      </div>

      {/* GSC lock + diagnostics grid */}
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 mb-6 flex items-center gap-3" data-testid="gsc-locked">
        <Lock className="h-5 w-5 text-slate-400" />
        <div>
          <p className="font-semibold text-slate-700 text-sm">Striking Distance & CTR panels are locked</p>
          <p className="text-xs text-slate-500">Connect Google Search Console (OAuth or CSV upload) to light up #8–30 keywords, impressions and CTR.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Orphan Pages (0 inbound links)" icon={Unlink} testid="seo-orphans" empty="No orphan pages — everything is linked." count={data.orphans.length}>
          {data.orphans.map((o) => (
            <div key={o.url} className="py-2 border-b border-slate-50 last:border-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{o.label}</p>
              <p className="text-xs text-slate-400">{o.type} · {o.url}</p>
            </div>
          ))}
        </Card>

        <Card title="Internal Link Opportunities" icon={Link2} testid="seo-links" empty="All key pages well-linked." count={data.internal_link_opportunities.length}>
          {data.internal_link_opportunities.map((l) => (
            <div key={l.page} className="py-2 border-b border-slate-50 last:border-0">
              <p className="text-sm font-semibold text-slate-800">{l.label} <span className="text-xs font-normal text-slate-400">· {l.inbound_links} inbound</span></p>
              {l.suggested_sources.length > 0 && <p className="text-xs text-slate-400">Link from: {l.suggested_sources.map((s) => s.label).join(" · ")}</p>}
            </div>
          ))}
        </Card>

        <Card title="Cannibalization" icon={AlertTriangle} testid="seo-cannibal" empty="No competing pages detected." count={data.cannibalization.length}>
          {data.cannibalization.map((c) => (
            <div key={c.keyword} className="py-2 border-b border-slate-50 last:border-0">
              <p className="text-sm font-semibold text-amber-700">"{c.keyword}" — {c.pages.length} pages</p>
              <p className="text-xs text-slate-400 truncate">{c.pages.map((p) => p.url).join("  ·  ")}</p>
            </div>
          ))}
        </Card>

        <Card title="Content Gaps" icon={Layers} testid="seo-gaps" empty="No gaps." count={data.content_gaps.length}>
          {data.content_gaps.map((g) => (
            <div key={g.pillar} className="py-2 border-b border-slate-50 last:border-0">
              <p className="text-sm font-semibold text-slate-800">{g.pillar} <span className="text-xs font-normal text-slate-400">· {g.published} live / {g.queued} queued</span></p>
              {g.sample.length > 0 && <p className="text-xs text-slate-400">{g.sample.join(" · ")}</p>}
            </div>
          ))}
        </Card>

        <Card title="On-Page QC" icon={FileWarning} testid="seo-qc" empty="No on-page issues." count={data.onpage_qc.length}>
          {data.onpage_qc.map((q) => (
            <div key={q.page} className="py-2 border-b border-slate-50 last:border-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{q.label}</p>
              <p className="text-xs text-red-500">{q.issues.join(" · ")}</p>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "warn" ? "text-amber-600" : tone === "ok" ? "text-emerald-600" : "text-navy";
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-5 py-3 min-w-[120px]">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function Card({ title, icon: Icon, testid, empty, count, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <h3 className="font-bold text-navy flex items-center gap-2 mb-2"><Icon className="h-4 w-4 text-cool" /> {title} <span className="text-xs font-normal text-slate-400">({count})</span></h3>
      <div className="max-h-72 overflow-y-auto">{count === 0 ? <p className="text-sm text-slate-400 py-4">{empty}</p> : children}</div>
    </div>
  );
}
